import type { CSSProperties } from "react";
import type { ViewStyle } from "react-native";

/** HTML5 native controls — hide browser “Save video” / download where supported. */
export const HTML5_VIDEO_CONTROLS_LIST = "nodownload";

/** IFrame API / react-player — hide native YouTube chrome; use YoutubePlayerChrome controls. */
export const YOUTUBE_IFRAME_PLAYER_VARS: Record<string, string | number> = {
  autoplay: 1,
  playsinline: 1,
  rel: 0,
  modestbranding: 1,
  enablejsapi: 1,
  controls: 0,
  disablekb: 1,
  fs: 0,
  iv_load_policy: 3,
  cc_load_policy: 0,
  /** Hide title/uploader overlay on hover inside the iframe where supported. */
  showinfo: 0,
};

/** react-native-youtube-iframe — same restrictions as iframe API. */
export const YOUTUBE_INITIAL_PLAYER_PARAMS = {
  controls: false,
  preventFullScreen: true,
  rel: false,
  iv_load_policy: 3,
} as const;

const YOUTUBE_EMBED_QUERY: Record<string, string> = {
  autoplay: "1",
  playsinline: "1",
  rel: "0",
  modestbranding: "1",
  enablejsapi: "1",
  controls: "0",
  disablekb: "1",
  fs: "0",
  iv_load_policy: "3",
  cc_load_policy: "0",
  showinfo: "0",
};

export function buildYoutubeEmbedUrl(
  videoId: string,
  extra?: Record<string, string | number>,
): string {
  const params = new URLSearchParams({ ...YOUTUBE_EMBED_QUERY });
  if (extra) {
    for (const [key, value] of Object.entries(extra)) {
      params.set(key, String(value));
    }
  }
  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
}

/** Block taps that open youtube.com watch / channel pages inside the WebView. */
export function isAllowedYoutubeWebViewNavigation(url: string): boolean {
  if (!url || url === "about:blank") return true;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    if (host === "lonelycpp.github.io") return true;
    if (host === "youtube.com" || host === "m.youtube.com") {
      return (
        u.pathname.startsWith("/embed/") ||
        u.pathname.includes("/iframe_api") ||
        u.pathname === "/"
      );
    }
    if (host === "youtu.be") return false;
    return true;
  } catch {
    return false;
  }
}

export const youtubePointerShieldStyle: ViewStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 2,
};

export const youtubePointerShieldCss: CSSProperties = {
  position: "absolute",
  inset: 0,
  zIndex: 2,
  cursor: "default",
  pointerEvents: "auto",
};

/** Prevent the YouTube iframe from stealing taps meant for custom chrome. */
export const youtubeEmbedHostCss: CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  pointerEvents: "none",
};

export const youtubeEndedOverlayCss: CSSProperties = {
  position: "absolute",
  inset: 0,
  zIndex: 4,
  backgroundColor: "#000",
  pointerEvents: "auto",
};

export const youtubeEndedOverlayStyle: ViewStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 3,
  backgroundColor: "#000",
};
